import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Plug, Megaphone, MessageSquare, AlertTriangle } from "lucide-react";
import { API, authHeaders } from "./shared";
import SupplierConnectPanel from "./SupplierConnectPanel";
import ClientBlastModal from "./ClientBlastModal";
import SmsBlastModal from "./SmsBlastModal";

const METHODS = [
  { value: "shopify", label: "Shopify" },
  { value: "woocommerce", label: "WooCommerce" },
  { value: "api", label: "API" },
  { value: "csv", label: "CSV / Feed" },
  { value: "manual", label: "Manual" },
];
const STATUSES = ["not_connected", "pending", "connected", "error"];
const ROUTING = ["inactive", "active", "paused"];

const statusColor = {
  connected: "bg-green-500/20 text-green-400 border-green-500/40",
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/40",
  error: "bg-red-500/20 text-red-400 border-red-500/40",
  not_connected: "bg-brand-surface text-brand-text-muted border-brand-border",
};
const routingColor = {
  active: "text-green-400",
  paused: "text-yellow-400",
  inactive: "text-brand-text-muted",
};

const EMPTY = { name: "", integration_method: "manual", connection_status: "not_connected", product_count: 0, order_routing_status: "inactive", shipping_method: "", notes: "" };

const SupplierForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full bg-brand-bg border border-brand-border text-brand-text font-sans text-sm px-3 py-2 focus:border-brand-gold outline-none";
  return (
    <div className="border border-brand-gold/50 bg-brand-surface p-5 mb-6" data-testid="supplier-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-sans text-xs text-brand-text-muted">Supplier Name *</label>
          <input data-testid="supplier-name-input" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. StarHair" />
        </div>
        <div>
          <label className="font-sans text-xs text-brand-text-muted">Integration Method</label>
          <select data-testid="supplier-method-select" className={inputCls} value={form.integration_method} onChange={(e) => set("integration_method", e.target.value)}>
            {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label className="font-sans text-xs text-brand-text-muted">Connection Status</label>
          <select data-testid="supplier-status-select" className={inputCls} value={form.connection_status} onChange={(e) => set("connection_status", e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </div>
        <div>
          <label className="font-sans text-xs text-brand-text-muted">Order Routing</label>
          <select data-testid="supplier-routing-select" className={inputCls} value={form.order_routing_status} onChange={(e) => set("order_routing_status", e.target.value)}>
            {ROUTING.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="font-sans text-xs text-brand-text-muted">Product Count</label>
          <input data-testid="supplier-count-input" type="number" min="0" className={inputCls} value={form.product_count} onChange={(e) => set("product_count", parseInt(e.target.value || "0", 10))} />
        </div>
        <div>
          <label className="font-sans text-xs text-brand-text-muted">Shipping Method</label>
          <input data-testid="supplier-shipping-input" className={inputCls} value={form.shipping_method} onChange={(e) => set("shipping_method", e.target.value)} placeholder="e.g. Supplier ships direct" />
        </div>
        <div className="md:col-span-2">
          <label className="font-sans text-xs text-brand-text-muted">Notes</label>
          <textarea data-testid="supplier-notes-input" rows={2} className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>
      </div>
      <div className="flex gap-3 mt-4">
        <button data-testid="supplier-save-btn" onClick={() => onSave(form)} className="px-5 py-2 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors">Save Supplier</button>
        <button data-testid="supplier-cancel-btn" onClick={onCancel} className="px-5 py-2 border border-brand-border text-brand-text-muted font-sans text-xs hover:border-brand-gold transition-colors">Cancel</button>
      </div>
    </div>
  );
};

const SuppliersTab = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | supplier object
  const [connectingId, setConnectingId] = useState(null);
  const [showBlast, setShowBlast] = useState(false);
  const [showSmsBlast, setShowSmsBlast] = useState(false);
  const [syncAlerts, setSyncAlerts] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/suppliers`, authHeaders())
      .then((r) => setSuppliers(r.data))
      .catch((e) => console.error("Failed to load suppliers:", e));
    axios.get(`${API}/admin/supplier-sync-alerts`, authHeaders())
      .then((r) => setSyncAlerts(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    if (!form.name.trim()) { toast.error("Supplier name is required"); return; }
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/suppliers`, form, authHeaders());
        toast.success("Supplier added");
      } else {
        await axios.put(`${API}/admin/suppliers/${editing.id}`, form, authHeaders());
        toast.success("Supplier updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      console.error("Failed to save supplier:", e);
      toast.error(e.response?.data?.detail || "Failed to save supplier");
    }
  };

  const remove = async (sup) => {
    if (!window.confirm(`Delete supplier "${sup.name}"? This only removes the integration record.`)) return;
    try {
      await axios.delete(`${API}/admin/suppliers/${sup.id}`, authHeaders());
      toast.success("Supplier deleted");
      load();
    } catch (e) {
      console.error("Failed to delete supplier:", e);
      toast.error("Failed to delete supplier");
    }
  };

  return (
    <div data-testid="suppliers-tab">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-serif text-2xl text-brand-text">Supplier Integration Center</h2>
        <div className="flex gap-2">
          <button data-testid="sms-blast-btn" onClick={() => setShowSmsBlast(true)} className="flex items-center gap-2 px-4 py-2 border border-brand-gold text-brand-gold font-sans text-xs font-semibold hover:bg-brand-gold/10 transition-colors">
            <MessageSquare size={14} /> SMS Blast
          </button>
          <button data-testid="client-blast-btn" onClick={() => setShowBlast(true)} className="flex items-center gap-2 px-4 py-2 border border-brand-gold text-brand-gold font-sans text-xs font-semibold hover:bg-brand-gold/10 transition-colors">
            <Megaphone size={14} /> Client Blast
          </button>
          {editing === null && (
            <button data-testid="add-supplier-btn" onClick={() => setEditing("new")} className="flex items-center gap-2 px-4 py-2 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors">
              <Plus size={14} /> Add Supplier
            </button>
          )}
        </div>
      </div>
      <p className="font-sans text-xs text-brand-text-muted mb-6">
        Structure only — no external accounts are connected and no orders are placed. Connect each supplier when you're ready.
      </p>

      {showBlast && <ClientBlastModal onClose={() => setShowBlast(false)} />}
      {showSmsBlast && <SmsBlastModal onClose={() => setShowSmsBlast(false)} />}

      {syncAlerts && syncAlerts.alerts.length > 0 && (
        <div data-testid="sync-alert-banner" className="mb-6 border border-amber-500/40 bg-amber-500/5 p-4">
          <p className="font-sans text-sm text-amber-400 font-semibold flex items-center gap-2 mb-1.5">
            <AlertTriangle size={14} /> Product sync overdue
          </p>
          <ul className="space-y-1">
            {syncAlerts.alerts.map((a) => (
              <li key={a.id} data-testid={`sync-alert-${a.id}`} className="font-sans text-xs text-brand-text-muted">
                <span className="text-brand-text font-medium">{a.name}</span>
                {a.reason === "never_synced"
                  ? " is connected but has never synced products."
                  : ` last synced ${a.days_since} days ago (${a.product_count} products may be outdated).`}
                {" "}Open its connection panel (plug icon) and run <span className="text-brand-gold">Sync Products</span>.
              </li>
            ))}
          </ul>
        </div>
      )}

      {editing !== null && (
        <SupplierForm initial={editing === "new" ? {} : editing} onSave={save} onCancel={() => setEditing(null)} />
      )}

      {connectingId && suppliers.find((s) => s.id === connectingId) && (
        <SupplierConnectPanel
          supplier={suppliers.find((s) => s.id === connectingId)}
          onClose={() => setConnectingId(null)}
          onChanged={load}
        />
      )}

      <div className="overflow-x-auto border border-brand-border">
        <table className="w-full text-left" data-testid="suppliers-table">
          <thead>
            <tr className="bg-brand-surface border-b border-brand-border">
              {["Supplier", "Method", "Status", "Products", "Last Sync", "Order Routing", "Shipping", "Notes", ""].map((h) => (
                <th key={h} className="font-sans text-[10px] uppercase tracking-wider text-brand-text-muted px-3 py-2.5 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {suppliers.map((s) => (
              <tr key={s.id} data-testid={`supplier-row-${s.id}`} className="border-b border-brand-border/40 hover:bg-brand-surface/50 transition-colors">
                <td className="px-3 py-3 font-sans text-sm text-brand-text font-medium whitespace-nowrap">{s.name}</td>
                <td className="px-3 py-3 font-sans text-xs text-brand-gold uppercase">{METHODS.find((m) => m.value === s.integration_method)?.label || s.integration_method}</td>
                <td className="px-3 py-3">
                  <span className={`font-sans text-[10px] uppercase px-2 py-1 border ${statusColor[s.connection_status] || statusColor.not_connected}`}>
                    {s.connection_status.replace("_", " ")}
                  </span>
                </td>
                <td className="px-3 py-3 font-sans text-sm text-brand-text">{s.product_count}</td>
                <td className="px-3 py-3 font-sans text-xs text-brand-text-muted whitespace-nowrap">{s.last_inventory_sync ? new Date(s.last_inventory_sync).toLocaleString() : "Never"}</td>
                <td className={`px-3 py-3 font-sans text-xs uppercase ${routingColor[s.order_routing_status] || ""}`}>{s.order_routing_status}</td>
                <td className="px-3 py-3 font-sans text-xs text-brand-text-muted max-w-[160px] truncate">{s.shipping_method}</td>
                <td className="px-3 py-3 font-sans text-xs text-brand-text-muted max-w-[220px] truncate" title={s.notes}>{s.notes}</td>
                <td className="px-3 py-3 whitespace-nowrap">
                  {s.integration_method === "woocommerce" && (
                    <button data-testid={`connect-supplier-${s.id}`} onClick={() => { setConnectingId(s.id); setEditing(null); }} title="Manage WooCommerce connection" className="p-1.5 text-green-400 hover:bg-green-500/10 transition-colors mr-1"><Plug size={14} /></button>
                  )}
                  <button data-testid={`edit-supplier-${s.id}`} onClick={() => setEditing(s)} className="p-1.5 text-brand-gold hover:bg-brand-gold/10 transition-colors mr-1"><Pencil size={14} /></button>
                  <button data-testid={`delete-supplier-${s.id}`} onClick={() => remove(s)} className="p-1.5 text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {suppliers.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-center font-sans text-sm text-brand-text-muted">No suppliers yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SuppliersTab;
